import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/price-movements';

const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;

        if (status === 401) {
            return {
                success: false,
                status,
                errorCode: data.error || 'UNAUTHORIZED',
                message: data.message || 'Authentication failed',
                isAuthError: true
            };
        }

        if (status === 403) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SUBSCRIPTION_REQUIRED',
                message: data.message || 'A Snipe Pro or Snipe Elite subscription is required to access price movements data. Please upgrade your plan.',
                isSubscriptionError: true
            };
        }

        if (status === 400) {
            return {
                success: false,
                status,
                errorCode: data.error || 'VALIDATION_ERROR',
                message: data.message || 'Invalid request parameters',
                details: data.errors || []
            };
        }

        if (status === 429) {
            return {
                success: false,
                status,
                errorCode: data.error || 'RATE_LIMIT_EXCEEDED',
                message: data.message || 'Too many price movements requests. Please try again in a minute.'
            };
        }

        if (status === 503) {
            const code = data.error || 'SERVICE_UNAVAILABLE';
            const isNoData = code === 'NO_DATA';
            return {
                success: false,
                status,
                errorCode: code,
                message: data.message || (isNoData
                    ? 'No price movements data is currently available. Data may be updating.'
                    : 'Price movements service is temporarily unavailable. Please try again later.'),
                isNoData
            };
        }

        if (status === 500) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SERVER_ERROR',
                message: data.message || 'Server error. Please try again later.'
            };
        }

        return {
            success: false,
            status,
            errorCode: data.error || 'SERVER_ERROR',
            message: data.message || 'Server error. Please try again later.'
        };
    }

    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.'
    };
};

const validatePayload = (data) => {
    if (!data || typeof data !== 'object') return false;
    if (!Array.isArray(data.props)) return false;
    return true;
};

const priceMovementsService = {
    getPriceMovements: async ({ player_name, prop_type, sport = 'afl' } = {}) => {
        if (!player_name || !prop_type) {
            return {
                success: false,
                errorCode: 'VALIDATION_ERROR',
                message: 'player_name and prop_type are required.'
            };
        }

        try {
            const response = await axios.get(`${API_URL}${BASE_PATH}`, {
                params: { sport, player_name, prop_type },
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result || result.success === false) {
                return {
                    success: false,
                    message: result?.message || 'Failed to fetch price movements'
                };
            }

            if (!validatePayload(result.data)) {
                return {
                    success: false,
                    errorCode: 'INVALID_RESPONSE',
                    message: 'Price movements response was malformed.'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    }
};

export default priceMovementsService;
